/** Gamepad preferences (localStorage). */

export type GamepadClickSource = "rt" | "a" | "both";

export const GP_SENSITIVITY_KEY = "settingGamepadSensitivity";
export const GP_DEADZONE_KEY = "settingGamepadDeadzone";
export const GP_CLICK_KEY = "settingGamepadClickSource";
export const GP_PAUSE_BTN_KEY = "settingGamepadPauseBtn";
export const GP_RETURN_BTN_KEY = "settingGamepadReturnBtn";
export const GP_MENU_RETURN_BTN_KEY = "settingGamepadMenuReturnBtn";
export const GP_MENU_BTN_KEY = "settingGamepadMenuBtn";
export const GP_RESTART_BTN_KEY = "settingGamepadRestartBtn";

/** Cursor max speed scale (1–20), same feel as touch joystick sensitivity. */
export const GP_SENSITIVITY_DEFAULT = 8;
export const GP_SENSITIVITY_MIN = 1;
export const GP_SENSITIVITY_MAX = 20;

/** Stick deadzone as 0–100 (% of full tilt). Default 12%. */
export const GP_DEADZONE_DEFAULT = 12;
export const GP_DEADZONE_MIN = 0;
export const GP_DEADZONE_MAX = 40;

/** Standard Gamepad button indices. */
export const GP_BTN = {
    A: 0,
    B: 1,
    X: 2,
    Y: 3,
    LB: 4,
    RB: 5,
    LT: 6,
    RT: 7,
    SELECT: 8,
    START: 9,
    L3: 10,
    R3: 11,
    UP: 12,
    DOWN: 13,
    LEFT: 14,
    RIGHT: 15,
} as const;

export const GP_CLICK_DEFAULT: GamepadClickSource = "both";
export const GP_PAUSE_BTN_DEFAULT = GP_BTN.START;
/** Return during gameplay (Select/Back — frees B for Superdot). */
export const GP_RETURN_BTN_DEFAULT = GP_BTN.SELECT;
/** Return / back while in menus (Xbox B). */
export const GP_MENU_RETURN_BTN_DEFAULT = GP_BTN.B;
/** Toggle sidebar Menu (Xbox LB — avoids face-button conflicts with Superdot). */
export const GP_MENU_BTN_DEFAULT = GP_BTN.LB;
/** Hold this button ~0.85s for quick restart (default RB). */
export const GP_RESTART_BTN_DEFAULT = GP_BTN.RB;

function clampNum(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function readNum(key: string, fallback: number, min: number, max: number) {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === "") return fallback;
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    return clampNum(n, min, max);
}

function readInt(key: string, fallback: number, min: number, max: number) {
    return Math.round(readNum(key, fallback, min, max));
}

export function getGamepadSensitivity(): number {
    return readInt(GP_SENSITIVITY_KEY, GP_SENSITIVITY_DEFAULT, GP_SENSITIVITY_MIN, GP_SENSITIVITY_MAX);
}

export function setGamepadSensitivity(v: number) {
    const n = clampNum(Math.round(v), GP_SENSITIVITY_MIN, GP_SENSITIVITY_MAX);
    localStorage.setItem(GP_SENSITIVITY_KEY, String(n));
    return n;
}

/** Deadzone as fraction 0–1. */
export function getGamepadDeadzone(): number {
    return readInt(GP_DEADZONE_KEY, GP_DEADZONE_DEFAULT, GP_DEADZONE_MIN, GP_DEADZONE_MAX) / 100;
}

export function getGamepadDeadzonePercent(): number {
    return readInt(GP_DEADZONE_KEY, GP_DEADZONE_DEFAULT, GP_DEADZONE_MIN, GP_DEADZONE_MAX);
}

export function setGamepadDeadzonePercent(v: number) {
    const n = clampNum(Math.round(v), GP_DEADZONE_MIN, GP_DEADZONE_MAX);
    localStorage.setItem(GP_DEADZONE_KEY, String(n));
    return n;
}

export function getGamepadClickSource(): GamepadClickSource {
    const raw = localStorage.getItem(GP_CLICK_KEY);
    if (raw === "rt" || raw === "a" || raw === "both") return raw;
    return GP_CLICK_DEFAULT;
}

export function setGamepadClickSource(v: GamepadClickSource) {
    localStorage.setItem(GP_CLICK_KEY, v);
}

export function getGamepadPauseBtn(): number {
    return readInt(GP_PAUSE_BTN_KEY, GP_PAUSE_BTN_DEFAULT, 0, 16);
}

export function setGamepadPauseBtn(v: number) {
    localStorage.setItem(GP_PAUSE_BTN_KEY, String(clampNum(Math.round(v), 0, 16)));
}

export function getGamepadReturnBtn(): number {
    return readInt(GP_RETURN_BTN_KEY, GP_RETURN_BTN_DEFAULT, 0, 16);
}

export function setGamepadReturnBtn(v: number) {
    localStorage.setItem(GP_RETURN_BTN_KEY, String(clampNum(Math.round(v), 0, 16)));
}

export function getGamepadMenuReturnBtn(): number {
    return readInt(GP_MENU_RETURN_BTN_KEY, GP_MENU_RETURN_BTN_DEFAULT, 0, 16);
}

export function setGamepadMenuReturnBtn(v: number) {
    localStorage.setItem(GP_MENU_RETURN_BTN_KEY, String(clampNum(Math.round(v), 0, 16)));
}

export function getGamepadMenuBtn(): number {
    return readInt(GP_MENU_BTN_KEY, GP_MENU_BTN_DEFAULT, 0, 16);
}

export function setGamepadMenuBtn(v: number) {
    localStorage.setItem(GP_MENU_BTN_KEY, String(clampNum(Math.round(v), 0, 16)));
}

export function getGamepadRestartBtn(): number {
    return readInt(GP_RESTART_BTN_KEY, GP_RESTART_BTN_DEFAULT, 0, 16);
}

export function setGamepadRestartBtn(v: number) {
    localStorage.setItem(GP_RESTART_BTN_KEY, String(clampNum(Math.round(v), 0, 16)));
}

export const GAMEPAD_SETTINGS_KEYS = [
    GP_SENSITIVITY_KEY,
    GP_DEADZONE_KEY,
    GP_CLICK_KEY,
    GP_PAUSE_BTN_KEY,
    GP_RETURN_BTN_KEY,
    GP_MENU_RETURN_BTN_KEY,
    GP_MENU_BTN_KEY,
    GP_RESTART_BTN_KEY,
] as const;

export function gamepadSettingsFallback(key: string): string {
    switch (key) {
        case GP_SENSITIVITY_KEY:
            return String(GP_SENSITIVITY_DEFAULT);
        case GP_DEADZONE_KEY:
            return String(GP_DEADZONE_DEFAULT);
        case GP_CLICK_KEY:
            return GP_CLICK_DEFAULT;
        case GP_PAUSE_BTN_KEY:
            return String(GP_PAUSE_BTN_DEFAULT);
        case GP_RETURN_BTN_KEY:
            return String(GP_RETURN_BTN_DEFAULT);
        case GP_MENU_RETURN_BTN_KEY:
            return String(GP_MENU_RETURN_BTN_DEFAULT);
        case GP_MENU_BTN_KEY:
            return String(GP_MENU_BTN_DEFAULT);
        case GP_RESTART_BTN_KEY:
            return String(GP_RESTART_BTN_DEFAULT);
        default:
            return "";
    }
}
